import {
  ACHIEVEMENT_CATALOG,
  ALPHA_CURRICULUM_POLICY,
  DEFAULT_FACT_MASTERY_RULES,
  DEFAULT_FLUENCY_POLICY,
  FACT_CATALOG,
  FACT_CATALOG_VERSION,
  SESSION_BUCKETS,
  SESSION_MODE_CONFIG,
  SKILL_MASTERY_STAGES,
  SKILLS,
  STRATEGIES,
  assessStrategyLesson,
  answerExposureFactIds,
  buildFactHelp,
  buildRetrievalBaseline,
  classifyRetrievalFluency,
  evaluateAchievements,
  evaluateFactMastery,
  evaluateSkillMastery,
  evaluateStrategyMastery,
  instantiateStrategyLesson,
  planNext,
  selectCurriculumOpportunities,
  emptySchedulingState,
  prepareSchedulingWindow,
  reserveFact,
  exposeFact,
  finishScheduledFact,
  reserveStrategy,
  finishScheduledStrategy,
  SCHEDULING_POLICY,
  type SchedulingState,
  validateAttempt,
  type AchievementAward,
  type Attempt,
  type Fact,
  type FactMastery,
  type Fluency,
  type SessionBucket,
  type SessionMode,
  type SkillId,
  type SkillMastery,
  type StrategyEvidence,
  type StrategyLessonResponse,
  type StrategyId,
  type StrategyMastery,
} from "@oefenheld/reken-core";
import {
  PROFILE_SCHEMA_VERSION,
  chooseResumableSession,
  sessionAcceptsAnswers,
  factMasteryKey,
  skillMasteryKey,
  strategyMasteryKey,
  type ItemPresentation,
  type LearnerProfile,
  type LearningRepository,
  type SelectorDecisionRecord,
  type SessionRecord,
  type StoredFactMastery,
  type StoredSkillMastery,
  type StoredStrategyMastery,
  type SchedulingCommit,
  type LessonProgress,
} from "@oefenheld/profile";

export interface RuntimeClock { now(): string; }
export interface RuntimeIds { next(prefix: string): string; }
export interface RekenheldRuntimeOptions {
  readonly repository: LearningRepository;
  readonly clock?: RuntimeClock;
  readonly ids?: RuntimeIds;
}
export interface StartSessionInput {
  readonly profileId: string;
  readonly mode: SessionMode;
  readonly at?: string;
}
export interface PresentedLearningItem {
  readonly presentation: ItemPresentation;
  readonly decision: SelectorDecisionRecord;
  readonly fact: Fact | null;
}
export type NextLearningItem = { readonly kind: "ITEM"; readonly item: PresentedLearningItem }
  | { readonly kind: "DONE_FOR_NOW" | "CONTENT_GAP"; readonly session: SessionRecord; readonly trace: readonly string[] };

export class NoLearningOpportunity extends Error {
  readonly reason: "DONE_FOR_NOW" | "CONTENT_GAP";
  readonly trace: readonly string[];
  constructor(reason: "DONE_FOR_NOW" | "CONTENT_GAP", trace: readonly string[]) {
    super(reason); this.reason = reason; this.trace = trace;
  }
}

function schedulingCommit(state: SchedulingState): SchedulingCommit {
  return { expectedRevision: state.revision, state: { ...state, revision: state.revision + 1 } };
}
export interface SubmitAnswerInput {
  readonly presentationId: string;
  readonly enteredAnswer: number | null;
  readonly firstInputAt: string | null;
  readonly submittedAt?: string;
  readonly editCount: number;
  readonly helpRequested: boolean;
  readonly supportUsed: boolean;
  readonly teachingStrategyShown?: StrategyId | null;
  readonly strategyId?: StrategyId | null;
}
export interface SubmitAnswerResult {
  readonly attempt: Attempt;
  readonly factMastery: StoredFactMastery;
  readonly skillMastery: StoredSkillMastery;
  readonly achievementUnlocks: readonly AchievementAward[];
  readonly session: SessionRecord;
}
export interface CompleteStrategyActivityInput {
  readonly presentationId: string;
  readonly response: StrategyLessonResponse;
  readonly occurredAt?: string;
}
export interface CompleteStrategyActivityResult {
  readonly strategyMastery: StoredStrategyMastery;
  readonly skillMastery: StoredSkillMastery;
  readonly achievementUnlocks: readonly AchievementAward[];
  readonly session: SessionRecord;
}

class SystemClock implements RuntimeClock {
  now(): string { return new Date().toISOString(); }
}
class RandomIds implements RuntimeIds {
  next(prefix: string): string { return `${prefix}-${globalThis.crypto.randomUUID()}`; }
}

function localDate(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(iso));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function chooseBucket(session: SessionRecord): SessionBucket {
  const weights = SESSION_MODE_CONFIG[session.mode].bucketWeights;
  const nextTotal = Object.values(session.bucketCounts).reduce((sum, value) => sum + value, 0) + 1;
  return SESSION_BUCKETS
    .map((bucket) => ({ bucket, deficit: weights[bucket] * nextTotal - session.bucketCounts[bucket] }))
    .sort((a, b) => b.deficit - a.deficit || SESSION_BUCKETS.indexOf(a.bucket) - SESSION_BUCKETS.indexOf(b.bucket))[0]
    .bucket;
}

function currentStageAtLeast(mastery: SkillMastery, stage: SkillMastery["currentStage"]): boolean {
  return SKILL_MASTERY_STAGES.indexOf(mastery.currentStage) >= SKILL_MASTERY_STAGES.indexOf(stage);
}

function resolvedPresentation(presentation: ItemPresentation, resolvedAt: string): ItemPresentation {
  return { ...presentation, state: "RESOLVED", resolvedAt };
}

function sessionAfterItem(
  session: SessionRecord,
  bucket: SessionBucket,
  difficult: boolean,
  helpRequested: boolean,
  incorrect: boolean,
  at: string,
  newUnlockIds: readonly string[],
  introducedStrategy: boolean,
): SessionRecord {
  const isCore = session.phase === "CORE_ACTIVE";
  let next: SessionRecord = {
    ...session,
    coreAttemptCount: session.coreAttemptCount + (isCore ? 1 : 0),
    bonusAttemptCount: session.bonusAttemptCount + (isCore ? 0 : 1),
    bucketCounts: { ...session.bucketCounts, [bucket]: session.bucketCounts[bucket] + 1 },
    consecutiveDifficultOutcomes: difficult ? session.consecutiveDifficultOutcomes + 1 : 0,
    strategyIntroductionCount: session.strategyIntroductionCount + (introducedStrategy ? 1 : 0),
    helpCount: session.helpCount + (helpRequested ? 1 : 0),
    errorCount: session.errorCount + (incorrect ? 1 : 0),
    achievementUnlockIds: [...new Set([...session.achievementUnlockIds, ...newUnlockIds])],
  };
  if (isCore) {
    const config = SESSION_MODE_CONFIG[session.mode];
    const elapsedMs = Date.parse(at) - Date.parse(session.startedAt);
    const complete = next.coreAttemptCount >= config.maximumCoreItems
      || (elapsedMs >= config.targetDurationMs && next.coreAttemptCount >= config.minimumCoreItems);
    if (complete) {
      next = {
        ...next,
        phase: next.achievementUnlockIds.length > 0 ? "REVEAL_PENDING" : "CORE_COMPLETE",
        bonusRoundOffered: true,
      };
    }
  }
  return next;
}

export function defaultLearnerProfile(
  id: string,
  displayName: string,
  now: string,
  timeZone = "Europe/Amsterdam",
): LearnerProfile {
  return {
    id, displayName, createdAt: now, updatedAt: now, timeZone,
    maximumAllowedRange: 20,
    currentAdaptiveRange: 20,
    schemaVersion: PROFILE_SCHEMA_VERSION,
  };
}

export class RekenheldRuntime {
  readonly #repository: LearningRepository;
  readonly #clock: RuntimeClock;
  readonly #ids: RuntimeIds;

  constructor(options: RekenheldRuntimeOptions) {
    this.#repository = options.repository;
    this.#clock = options.clock ?? new SystemClock();
    this.#ids = options.ids ?? new RandomIds();
  }

  async startSession(input: StartSessionInput): Promise<SessionRecord> {
    return this.#repository.withProfileLock(input.profileId, () => this.startSessionLocked(input));
  }
  private async startSessionLocked(input: StartSessionInput): Promise<SessionRecord> {
    const profile = await this.#repository.getProfile(input.profileId);
    if (!profile) throw new Error(`Onbekend leerlingprofiel: ${input.profileId}.`);
    const existing = await this.resumableSession(profile.id);
    if (existing && sessionAcceptsAnswers(existing)) return existing;
    const startedAt = input.at ?? this.#clock.now();
    const session: SessionRecord = {
      id: this.#ids.next("session"), profileId: profile.id, mode: input.mode,
      phase: "CORE_ACTIVE", startedAt, endedAt: null,
      localDate: localDate(startedAt, profile.timeZone), timeZone: profile.timeZone,
      voluntarilyStarted: true, coreAttemptCount: 0, bonusAttemptCount: 0,
      bucketCounts: { CORE: 0, MAINTENANCE: 0, CHALLENGE: 0 },
      consecutiveDifficultOutcomes: 0, strategyIntroductionCount: 0,
      helpCount: 0, errorCount: 0, bonusRoundOffered: false,
      bonusRoundAccepted: false, earlyExit: false, achievementUnlockIds: [],
      catalogVersion: FACT_CATALOG_VERSION, rulesetVersion: DEFAULT_FACT_MASTERY_RULES.version,
    };
    await this.#repository.putSession(session);
    return session;
  }

  async presentNext(sessionId: string, requestedSkillIds?: readonly SkillId[]): Promise<PresentedLearningItem> {
    return this.withSessionLock(sessionId, async () => {
      const effective = await this.recoverSessionLocked(sessionId);
      return this.presentNextLocked(effective.id, requestedSkillIds);
    });
  }

  async resumableSession(profileId: string): Promise<SessionRecord | null> {
    const [sessions, presentations] = await Promise.all([
      this.#repository.listSessionsForProfile(profileId), this.#repository.listPresentations(profileId),
    ]);
    return chooseResumableSession(sessions, presentations);
  }

  private async recoverSessionLocked(sessionId: string): Promise<SessionRecord> {
    const requested = await this.requireActiveSession(sessionId);
    await this.#repository.reconcilePresentations(requested.profileId, this.#clock.now());
    const [sessions, presentations] = await Promise.all([
      this.#repository.listSessionsForProfile(requested.profileId), this.#repository.listPresentations(requested.profileId),
    ]);
    return chooseResumableSession(sessions, presentations, sessionId) ?? requested;
  }

  /** Explicit terminal outcomes for UI; no catch-all fallback to unsafe content. */
  async nextItem(sessionId: string, requestedSkillIds?: readonly SkillId[]): Promise<NextLearningItem> {
    return this.withSessionLock(sessionId, async () => {
      const effective = await this.recoverSessionLocked(sessionId);
      try { return { kind: "ITEM", item: await this.presentNextLocked(effective.id, requestedSkillIds) }; }
      catch (error) {
        if (!(error instanceof NoLearningOpportunity)) throw error;
        const session = await this.requireActiveSession(effective.id);
        const next: SessionRecord = { ...session,
          phase: session.achievementUnlockIds.length && session.phase === "CORE_ACTIVE" ? "REVEAL_PENDING" : "CORE_COMPLETE",
          completionReason: error.reason, completionTrace: error.trace, bonusRoundOffered: false };
        const open = await this.#repository.listOpenPresentationsForSession(effective.id);
        await this.#repository.closeSession(next, open.map((p) => ({ ...p, state: "ABANDONED", resolvedAt: this.#clock.now() })));
        return { kind: error.reason, session: next, trace: error.trace };
      }
    });
  }

  private async presentNextLocked(sessionId: string, requestedSkillIds?: readonly SkillId[]): Promise<PresentedLearningItem> {
    const session = await this.requireActiveSession(sessionId);
    let scheduling = await this.readScheduling(session.profileId);
    const open = await this.#repository.listOpenPresentationsForSession(sessionId);
    if (open.length > 0) {
      let presentation = open[0];
      const decision = await this.#repository.getSelectorDecision(presentation.selectionDecisionId);
      if (!decision) throw new Error("Open presentatie mist het oorspronkelijke selectorbesluit.");
      if (presentation.factId === null && !presentation.strategyId) throw new NoLearningOpportunity("CONTENT_GAP", ["legacy concept activity missing"]);
      if (!presentation.schedulingVersion) {
        const lesson = presentation.strategyId ? instantiateStrategyLesson(presentation.strategyId, FACT_CATALOG, STRATEGIES,
          decision.seed, decision.reason === "STRATEGY_REFRESH" ? "REFRESH" : "FULL") : undefined;
        presentation = { ...presentation, lesson, schedulingVersion: SCHEDULING_POLICY.version };
        await this.#repository.commitProgress(presentation, schedulingCommit(scheduling));
      }
      return {
        presentation, decision,
        fact: presentation.factId === null ? null : FACT_CATALOG.factById.get(presentation.factId) ?? null,
      };
    }
    const config = SESSION_MODE_CONFIG[session.mode];
    if (session.phase === "CORE_ACTIVE" && (session.coreAttemptCount >= config.maximumCoreItems
      || (session.coreAttemptCount >= config.minimumCoreItems && Date.parse(this.#clock.now()) - Date.parse(session.startedAt) >= config.targetDurationMs))) {
      throw new NoLearningOpportunity("DONE_FOR_NOW", ["session boundary reached"]);
    }
    if (session.phase === "BONUS_ACTIVE" && session.bonusAttemptCount >= 4) throw new NoLearningOpportunity("DONE_FOR_NOW", ["bonus boundary reached"]);
    scheduling = prepareSchedulingWindow(scheduling, this.#clock.now());
    const [factMastery, strategyMastery, skillMastery, allAttempts] = await Promise.all([
      this.#repository.listFactMastery(session.profileId),
      this.#repository.listStrategyMastery(session.profileId),
      this.#repository.listSkillMastery(session.profileId),
      this.#repository.listAttempts(session.profileId),
    ]);
    const attempts = allAttempts.filter((attempt) => attempt.sessionId === session.id);
    const targetSkillIds = requestedSkillIds ?? selectCurriculumOpportunities(
      allAttempts.length, new Map(skillMastery.map((mastery) => [mastery.skillId, mastery])),
    );
    if (targetSkillIds.length === 0) throw new Error("Minimaal één target skill is vereist.");
    // Retention ages against the current clock, not the time of the last saved answer.
    const factMasteryById = new Map(factMastery.map((mastery) => [mastery.factId,
      evaluateFactMastery(mastery.factId, allAttempts.filter((a) => a.factId === mastery.factId), this.#clock.now())]));
    const strategyMasteryById = new Map(await Promise.all(strategyMastery.map(async (mastery) => [mastery.strategyId,
      evaluateStrategyMastery(mastery.strategyId, await this.#repository.listStrategyEvidence(session.profileId, mastery.strategyId), this.#clock.now())] as const)));
    const currentSkills = SKILLS.map((skill) => FACT_CATALOG.facts.some((f) => f.skillIds.includes(skill.id))
      ? this.projectSkill(skill.id, session.profileId, this.#clock.now(), factMasteryById, strategyMasteryById)
      : skillMastery.find((m) => m.skillId === skill.id));
    const masteredSkillIds = new Set(currentSkills.filter((m) => m && currentStageAtLeast(m, "CAN_DO")).map((m) => m!.skillId));
    const weakFactIds = new Set<string>(); // Legacy selector field, no priority based on stage alone.
    const dueFactIds = new Set([...factMasteryById.values()].filter((m) => m.retentionState === "DUE" || m.retentionState === "REFRESH_DUE").map((m) => m.factId));
    const requestedBucket = chooseBucket(session);
    const seed = `${session.id}:${session.coreAttemptCount + session.bonusAttemptCount + 1}`;
    const planned = planNext({
      catalog: FACT_CATALOG, skills: SKILLS, strategies: STRATEGIES, targetSkillIds, bucket: requestedBucket, seed, scheduling,
      snapshot: { factMasteryById, strategyMasteryById, masteredSkillIds, weakFactIds, dueFactIds },
      session: {
        mode: session.mode, coreItemIndex: session.coreAttemptCount,
        consecutiveDifficultOutcomes: session.consecutiveDifficultOutcomes,
        strategyIntroductionCount: session.strategyIntroductionCount,
        recentFactIds: attempts.slice(-6).map((attempt) => attempt.factId),
        cooldownFactIds: new Set(), // Durable exposure/dose gates cover errors and help across sessions.
      },
    });
    if (planned.kind !== "ITEM") throw new NoLearningOpportunity(planned.kind, planned.trace);
    const { decision, bucket, lesson } = planned;
    const now = this.#clock.now();
    if (decision.factId) scheduling = reserveFact(scheduling, decision.factId, now);
    if (lesson) {
      scheduling = reserveStrategy(scheduling, lesson.strategyId, lesson.variant === "FULL", now);
      for (const activity of lesson.activities) {
        if (!activity.factId) continue;
        if (activity.phase === "INDEPENDENT_CHECK") scheduling = reserveFact(scheduling, activity.factId, now);
        else for (const id of activity.answerExposureFactIds ?? [activity.factId]) scheduling = exposeFact(scheduling, id, now);
      }
    }
    const decisionId = this.#ids.next("decision");
    const record: SelectorDecisionRecord = {
      id: decisionId, profileId: session.profileId, sessionId: session.id, createdAt: now,
      reason: decision.selectionReason, factId: decision.factId,
      targetSkillId: decision.targetSkillId, strategyId: decision.strategyId,
      objective: decision.objective, responseMode: decision.responseMode,
      supportLevel: decision.supportLevel, seed: decision.seed,
      trace: [...decision.trace, `curriculum: ${requestedSkillIds ? "explicit target" : ALPHA_CURRICULUM_POLICY.version}`], bucket,
    };
    const presentation: ItemPresentation = {
      id: this.#ids.next("presentation"), profileId: session.profileId, sessionId: session.id,
      factId: decision.factId, targetSkillId: decision.targetSkillId, strategyId: decision.strategyId,
      selectionReason: decision.selectionReason, presentedAt: now, resolvedAt: null,
      state: "PRESENTED", selectionDecisionId: decisionId, bucket,
      lesson, schedulingVersion: SCHEDULING_POLICY.version,
    };
    await this.#repository.commitPresentation(record, presentation, schedulingCommit(scheduling));
    return {
      presentation, decision: record,
      fact: decision.factId === null ? null : FACT_CATALOG.factById.get(decision.factId) ?? null,
    };
  }

  async submitAnswer(input: SubmitAnswerInput): Promise<SubmitAnswerResult> {
    const presentation = await this.requireOpenPresentation(input.presentationId);
    return this.#repository.withProfileLock(presentation.profileId, () => this.submitAnswerLocked(input));
  }
  private async submitAnswerLocked(input: SubmitAnswerInput): Promise<SubmitAnswerResult> {
    const presentation = await this.requireOpenPresentation(input.presentationId);
    if (presentation.factId === null) {
      throw new Error("Deze presentatie is een strategieactiviteit, geen kale som.");
    }
    const fact = FACT_CATALOG.factById.get(presentation.factId);
    if (!fact) throw new Error(`Onbekende fact in presentatie: ${presentation.factId}.`);
    const decision = await this.requireDecision(presentation.selectionDecisionId);
    const session = await this.requireActiveSession(presentation.sessionId);
    const submittedAt = input.submittedAt ?? this.#clock.now();
    const schedulingBeforeAnswer = await this.readScheduling(session.profileId);
    const exposedAfterPresentation = Date.parse(schedulingBeforeAnswer.facts[fact.id]?.lastExposedAt ?? presentation.presentedAt) > Date.parse(presentation.presentedAt);
    const correct = input.helpRequested ? null : input.enteredAnswer === fact.answer;
    let attempt: Attempt = {
      id: this.#ids.next("attempt"), profileId: presentation.profileId,
      presentationId: presentation.id, attemptOrdinal: 1, sessionId: presentation.sessionId,
      factId: fact.id, factFamilyId: fact.familyId, targetSkillId: presentation.targetSkillId,
      objective: decision.objective, responseMode: decision.responseMode,
      supportLevel: decision.supportLevel, supportUsed: input.supportUsed || exposedAfterPresentation,
      presentedAt: presentation.presentedAt, firstInputAt: input.firstInputAt,
      submittedAt, enteredAnswer: input.enteredAnswer, correct, editCount: input.editCount,
      helpRequested: input.helpRequested,
      teachingStrategyShown: input.teachingStrategyShown ?? null,
      strategyId: input.strategyId ?? null, selectionReason: presentation.selectionReason,
      catalogVersion: FACT_CATALOG_VERSION, rulesetVersion: DEFAULT_FACT_MASTERY_RULES.version,
      fluency: "UNASSESSED", fluencyPolicyVersion: null,
    };
    if (attempt.responseMode === "DIRECT_RETRIEVAL") {
      const baseline = buildRetrievalBaseline(await this.#repository.listAttempts(session.profileId));
      const fluency: Fluency = classifyRetrievalFluency(attempt, baseline);
      attempt = {
        ...attempt, fluency,
        fluencyPolicyVersion: fluency === "UNASSESSED" ? null : DEFAULT_FLUENCY_POLICY.version,
      };
    }
    const validationIssues = validateAttempt(attempt);
    if (validationIssues.length > 0) {
      throw new Error(`Ongeldige attempt: ${validationIssues.map((issue) => issue.code).join(", ")}.`);
    }

    const previousAttempts = await this.#repository.listAttemptsForFact(session.profileId, fact.id);
    const factProjection: StoredFactMastery = {
      ...evaluateFactMastery(fact.id, [...previousAttempts, attempt], submittedAt),
      key: factMasteryKey(session.profileId, fact.id), profileId: session.profileId,
      updatedAt: submittedAt,
    };
    const allFactMastery = await this.#repository.listFactMastery(session.profileId);
    const factMasteryById = new Map<Fact["id"], FactMastery>(
      allFactMastery.map((mastery) => [mastery.factId, mastery]),
    );
    factMasteryById.set(fact.id, factProjection);
    const allStrategyMastery = await this.#repository.listStrategyMastery(session.profileId);
    const strategyMasteryById = new Map<StrategyId, StrategyMastery>(
      allStrategyMastery.map((mastery) => [mastery.strategyId, mastery]),
    );
    const skillProjection = this.projectSkill(
      presentation.targetSkillId, session.profileId, submittedAt,
      factMasteryById, strategyMasteryById,
    );
    const allAttempts = [...(await this.#repository.listAttempts(session.profileId)), attempt];
    const awards = await this.evaluateAwards(
      session.profileId, submittedAt, allAttempts, [skillProjection], [],
    );
    const nextSession = sessionAfterItem(
      session, presentation.bucket, correct !== true, input.helpRequested,
      correct === false, submittedAt, awards.map((award) => award.id), false,
    );
    await this.#repository.commitLearningResult({
      scheduling: schedulingCommit(finishScheduledFact(await this.readScheduling(session.profileId), fact.id,
        correct, submittedAt, presentation.bucket === "CORE", previousAttempts.length === 0,
        STRATEGIES.find((s) => s.appliesToSkillIds.includes(presentation.targetSkillId)
          && s.supportedOperations.includes(fact.operation) && fact.strategyTags.includes(s.id))?.id ?? null)),
      attempt, presentation: resolvedPresentation(presentation, submittedAt),
      factMastery: factProjection, strategyEvidence: [], strategyMastery: [],
      skillMastery: [skillProjection], achievementUnlocks: awards, session: nextSession,
    });
    return {
      attempt, factMastery: factProjection, skillMastery: skillProjection,
      achievementUnlocks: awards, session: nextSession,
    };
  }

  async completeStrategyActivity(
    input: CompleteStrategyActivityInput,
  ): Promise<CompleteStrategyActivityResult> {
    const presentation = await this.requireOpenPresentation(input.presentationId);
    return this.#repository.withProfileLock(presentation.profileId, () => this.completeStrategyActivityLocked(input));
  }
  private async completeStrategyActivityLocked(input: CompleteStrategyActivityInput): Promise<CompleteStrategyActivityResult> {
    const presentation = await this.requireOpenPresentation(input.presentationId);
    if (presentation.strategyId === null) {
      throw new Error("Deze conceptactiviteit heeft geen strategie om te projecteren.");
    }
    const session = await this.requireActiveSession(presentation.sessionId);
    const decision = await this.requireDecision(presentation.selectionDecisionId);
    const lesson = presentation.lesson ?? instantiateStrategyLesson(
      presentation.strategyId, FACT_CATALOG, STRATEGIES, decision.seed,
      decision.reason === "STRATEGY_REFRESH" ? "REFRESH" : "FULL",
    );
    const assessment = assessStrategyLesson(lesson, input.response);
    // A checkpointed answer cannot be changed later after its solution has been displayed.
    for (const [activityId, answers] of Object.entries(presentation.lessonProgress?.answers ?? {})) {
      const response = input.response.activities.find((a) => a.activityId === activityId);
      for (const [questionId, value] of Object.entries(answers)) {
        if (response?.answers[questionId] !== value) throw new Error("Lesantwoord wijkt af van opgeslagen voortgang.");
      }
    }
    for (const response of presentation.lessonProgress?.responses ?? []) {
      if (JSON.stringify(input.response.activities.find((a) => a.activityId === response.activityId)) !== JSON.stringify(response)) {
        throw new Error("Lesrespons wijkt af van opgeslagen voortgang.");
      }
    }
    const occurredAt = input.occurredAt ?? this.#clock.now();
    if (!Number.isFinite(Date.parse(occurredAt)) || Date.parse(occurredAt) < Date.parse(presentation.presentedAt)) {
      throw new Error("Ongeldig afrondtijdstip voor deze les.");
    }
    const strategyId = presentation.strategyId;
    const evidence: StrategyEvidence[] = assessment.evidenceKinds.map((kind) => ({
      id: this.#ids.next("strategy-evidence"), profileId: session.profileId,
      strategyId, sessionId: session.id, occurredAt, kind,
      presentationId: presentation.id, attemptId: null,
    }));
    const priorEvidence = await this.#repository.listStrategyEvidence(session.profileId, strategyId);
    const strategyProjection: StoredStrategyMastery = {
      ...evaluateStrategyMastery(strategyId, [...priorEvidence, ...evidence], occurredAt),
      key: strategyMasteryKey(session.profileId, strategyId), profileId: session.profileId,
      updatedAt: occurredAt,
    };
    const [allFactMastery, allStrategyMastery] = await Promise.all([
      this.#repository.listFactMastery(session.profileId),
      this.#repository.listStrategyMastery(session.profileId),
    ]);
    const strategyMasteryById = new Map<StrategyId, StrategyMastery>(
      allStrategyMastery.map((mastery) => [mastery.strategyId, mastery]),
    );
    strategyMasteryById.set(strategyId, strategyProjection);
    const skillProjection = this.projectSkill(
      presentation.targetSkillId, session.profileId, occurredAt,
      new Map(allFactMastery.map((mastery) => [mastery.factId, mastery])),
      strategyMasteryById,
    );
    const awards = await this.evaluateAwards(
      session.profileId, occurredAt, await this.#repository.listAttempts(session.profileId),
      [skillProjection], [strategyProjection], evidence,
    );
    const introduced = assessment.evidenceKinds.includes("INTRODUCTION_COMPLETED")
      && !priorEvidence.some((item) => item.kind === "INTRODUCTION_COMPLETED");
    const independentFailure = assessment.independentOutcome === "INCORRECT";
    const helpRequested = assessment.independentOutcome === "HELP";
    const nextSession = sessionAfterItem(
      session, presentation.bucket, independentFailure || helpRequested, helpRequested, independentFailure,
      occurredAt, awards.map((award) => award.id), introduced,
    );
    await this.#repository.commitStrategyLearning({
      scheduling: schedulingCommit(finishScheduledStrategy(
        finishScheduledFact(await this.readScheduling(session.profileId),
          lesson.activities.find((a) => a.phase === "INDEPENDENT_CHECK")!.factId!,
          helpRequested ? null : !independentFailure, occurredAt, false, false),
        strategyId, assessment.independentOutcome === "CORRECT",
        FACT_CATALOG.facts.filter((f) => f.strategyTags.includes(strategyId)).map((f) => f.id))),
      presentation: { ...resolvedPresentation(presentation, occurredAt), lessonResponse: input.response },
      strategyEvidence: evidence, strategyMastery: strategyProjection,
      skillMastery: [skillProjection], achievementUnlocks: awards, session: nextSession,
    });
    return {
      strategyMastery: strategyProjection, skillMastery: skillProjection,
      achievementUnlocks: awards, session: nextSession,
    };
  }

  async finishReveals(sessionId: string): Promise<SessionRecord> {
    return this.withSessionLock(sessionId, () => this.finishRevealsLocked(sessionId));
  }
  private async finishRevealsLocked(sessionId: string): Promise<SessionRecord> {
    const session = await this.#repository.getSession(sessionId);
    if (!session || session.phase !== "REVEAL_PENDING") {
      throw new Error("Er staan geen kaartonthullingen klaar.");
    }
    const next = { ...session, phase: "CORE_COMPLETE" as const };
    await this.#repository.putSession(next);
    return next;
  }

  async startBonus(sessionId: string): Promise<SessionRecord> {
    return this.withSessionLock(sessionId, () => this.startBonusLocked(sessionId));
  }
  private async startBonusLocked(sessionId: string): Promise<SessionRecord> {
    const session = await this.#repository.getSession(sessionId);
    if (!session || session.phase !== "CORE_COMPLETE" || !session.bonusRoundOffered || session.completionReason || session.bonusRoundAccepted) {
      throw new Error("Een bonusronde kan alleen na de kernsessie starten.");
    }
    const next = { ...session, phase: "BONUS_ACTIVE" as const, bonusRoundAccepted: true };
    await this.#repository.putSession(next);
    return next;
  }

  async stopSession(sessionId: string, at = this.#clock.now()): Promise<SessionRecord> {
    return this.withSessionLock(sessionId, () => this.stopSessionLocked(sessionId, at));
  }
  private async stopSessionLocked(sessionId: string, at: string): Promise<SessionRecord> {
    const session = await this.#repository.getSession(sessionId);
    if (!session || session.phase === "COMPLETE" || session.phase === "EARLY_EXIT") {
      throw new Error("Deze sessie kan niet worden gestopt.");
    }
    const earlyExit = session.phase === "CORE_ACTIVE";
    const next: SessionRecord = {
      ...session, phase: earlyExit ? "EARLY_EXIT" : "COMPLETE", endedAt: at, earlyExit,
    };
    const openPresentations = await this.#repository.listOpenPresentationsForSession(sessionId);
    await this.#repository.closeSession(
      next,
      openPresentations.map((presentation) => ({
        ...presentation,
        state: "ABANDONED" as const,
        resolvedAt: at,
      })),
    );
    return next;
  }

  async checkpointLesson(presentationId: string, progress: LessonProgress): Promise<void> {
    const initial = await this.requireOpenPresentation(presentationId);
    await this.#repository.withProfileLock(initial.profileId, async () => {
      const presentation = await this.requireOpenPresentation(presentationId);
      await this.requireActiveSession(presentation.sessionId);
      const lesson = presentation.lesson;
      if (!lesson) throw new Error("Lesinstantie ontbreekt.");
      if (!Number.isSafeInteger(progress.activityIndex) || progress.activityIndex < 0 || progress.activityIndex > lesson.activities.length
        || !Number.isSafeInteger(progress.questionIndex) || progress.questionIndex < 0) throw new Error("Ongeldige lespositie.");
      const activity = lesson.activities[progress.activityIndex];
      if (activity && progress.questionIndex >= Math.max(1, activity.questions.length)) throw new Error("Onbekende lesvraag.");
      const previous = presentation.lessonProgress;
      if (previous && (progress.activityIndex < previous.activityIndex
        || (progress.activityIndex === previous.activityIndex && progress.questionIndex < previous.questionIndex))) {
        throw new Error("Lesvoortgang is al verder; herlaad dit tabblad.");
      }
      for (const [id, answers] of Object.entries(progress.answers)) {
        const known = lesson.activities.find((a) => a.id === id);
        if (!known || Object.entries(answers).some(([key, value]) => !Number.isSafeInteger(value) || !known.questions.some((q) => q.id === key))) {
          throw new Error("Onbekend of ongeldig deelantwoord.");
        }
      }
      for (const [id, answers] of Object.entries(previous?.answers ?? {})) {
        for (const [key, value] of Object.entries(answers)) {
          if (progress.answers[id]?.[key] !== value) throw new Error("Een opgeslagen lesantwoord kan niet worden vervangen.");
        }
      }
      for (const response of previous?.responses ?? []) {
        if (JSON.stringify(progress.responses.find((r) => r.activityId === response.activityId)) !== JSON.stringify(response)) {
          throw new Error("Een opgeslagen lesrespons kan niet worden vervangen.");
        }
      }
      let scheduling = await this.readScheduling(presentation.profileId);
      if (activity?.factId) {
        const help = progress.responses.some((response) => response.activityId === activity.id && response.helpRequested);
        const exposures = activity.phase === "INDEPENDENT_CHECK" && !help ? [activity.factId]
          : activity.answerExposureFactIds ?? [activity.factId];
        for (const id of exposures) scheduling = exposeFact(scheduling, id, this.#clock.now());
      }
      await this.#repository.commitProgress({ ...presentation, lessonProgress: progress }, schedulingCommit(scheduling));
    });
  }

  /** Record the actual display of feedback/help, including a delayed screen in another tab. */
  async recordAnswerExposure(presentationId: string): Promise<void> {
    const presentation = await this.#repository.getPresentation(presentationId);
    if (!presentation?.factId || presentation.state !== "RESOLVED") throw new Error("Geen opgeloste som voor deze hulpweergave.");
    await this.#repository.withProfileLock(presentation.profileId, async () => {
      let state = await this.readScheduling(presentation.profileId);
      const fact = FACT_CATALOG.factById.get(presentation.factId!)!;
      const help = buildFactHelp(fact, STRATEGIES);
      for (const id of answerExposureFactIds(fact, help.strategyId, FACT_CATALOG)) state = exposeFact(state, id, this.#clock.now());
      await this.#repository.commitScheduling(schedulingCommit(state));
    });
  }

  private async withSessionLock<T>(sessionId: string, action: () => Promise<T>): Promise<T> {
    const session = await this.#repository.getSession(sessionId);
    if (!session) throw new Error(`Sessie ${sessionId} bestaat niet.`);
    return this.#repository.withProfileLock(session.profileId, action);
  }

  /** Lazy, conservative v1 import; preserve evidence and count already displayed work. */
  private async readScheduling(profileId: string): Promise<SchedulingState> {
    const stored = await this.#repository.getScheduling(profileId);
    if (stored) return stored;
    const [presentations, attempts] = await Promise.all([
      this.#repository.listPresentations(profileId), this.#repository.listAttempts(profileId),
    ]);
    let state = emptySchedulingState(profileId, presentations[0]?.presentedAt ?? this.#clock.now());
    const attemptByPresentation = new Map(attempts.map((a) => [a.presentationId, a]));
    for (const presentation of presentations) {
      const at = presentation.resolvedAt ?? presentation.presentedAt;
      state = prepareSchedulingWindow(state, at);
      if (presentation.factId) {
        const id = presentation.factId;
        // Historical excessive dosage is recorded as-is, never discarded to pass the new cap.
        state = { ...state, lastActivityAt: at,
          facts: { ...state.facts, [id]: { presentations: (state.facts[id]?.presentations ?? 0) + 1,
            successes: state.facts[id]?.successes ?? 0, difficulties: state.facts[id]?.difficulties ?? 0,
            eligibleAfterOrdinal: state.independentOrdinal + 3, lastExposedAt: at } },
          lifetimePresentations: { ...state.lifetimePresentations, [id]: (state.lifetimePresentations[id] ?? 0) + 1 } };
        const attempt = attemptByPresentation.get(presentation.id);
        if (attempt) state = finishScheduledFact(state, id, attempt.correct, at, presentation.bucket === "CORE", (state.lifetimePresentations[id] ?? 0) === 1,
          STRATEGIES.find((s) => s.appliesToSkillIds.includes(presentation.targetSkillId)
            && s.supportedOperations.includes(FACT_CATALOG.factById.get(id)!.operation))?.id ?? null);
      } else if (presentation.strategyId) {
        const decision = await this.#repository.getSelectorDecision(presentation.selectionDecisionId);
        const lesson = presentation.lesson ?? instantiateStrategyLesson(presentation.strategyId, FACT_CATALOG, STRATEGIES,
          decision?.seed ?? presentation.id, decision?.reason === "STRATEGY_REFRESH" ? "REFRESH" : "FULL");
        state = { ...state, lastActivityAt: at,
          strategyCounts: { ...state.strategyCounts, [lesson.strategyId]: (state.strategyCounts[lesson.strategyId] ?? 0) + 1 },
          introductions: state.introductions + (lesson.variant === "FULL" ? 1 : 0) };
        for (const activity of lesson.activities) {
          if (!activity.factId) continue;
          for (const id of activity.answerExposureFactIds ?? [activity.factId]) state = exposeFact(state, id, at);
          if (activity.phase === "INDEPENDENT_CHECK") {
            state = { ...state, facts: { ...state.facts, [activity.factId]: { ...state.facts[activity.factId],
              presentations: state.facts[activity.factId].presentations + 1 } },
              lifetimePresentations: { ...state.lifetimePresentations, [activity.factId]: (state.lifetimePresentations[activity.factId] ?? 0) + 1 } };
            if (presentation.lessonResponse) {
              const assessment = assessStrategyLesson(lesson, presentation.lessonResponse);
              state = finishScheduledFact(state, activity.factId, assessment.independentOutcome === "HELP" ? null : assessment.independentOutcome === "CORRECT", at, false, false);
              state = finishScheduledStrategy(state, lesson.strategyId, assessment.independentOutcome === "CORRECT", []);
            }
          }
        }
      }
    }
    return state;
  }

  private projectSkill(
    skillId: SkillId,
    profileId: string,
    now: string,
    factMasteryById: ReadonlyMap<string, FactMastery>,
    strategyMasteryById: ReadonlyMap<StrategyId, StrategyMastery>,
  ): StoredSkillMastery {
    const facts = FACT_CATALOG.facts
      .filter((fact) => fact.skillIds.includes(skillId))
      .map((fact) => factMasteryById.get(fact.id) ?? evaluateFactMastery(fact.id, [], now));
    const strategies = STRATEGIES
      .filter((strategy) => strategy.appliesToSkillIds.includes(skillId))
      .map((strategy) =>
        strategyMasteryById.get(strategy.id) ?? evaluateStrategyMastery(strategy.id, [], now)
      );
    return {
      ...evaluateSkillMastery(skillId, facts, strategies),
      key: skillMasteryKey(profileId, skillId), profileId, updatedAt: now,
    };
  }

  private async evaluateAwards(
    profileId: string,
    now: string,
    attempts: readonly Attempt[],
    changedSkills: readonly StoredSkillMastery[],
    changedStrategies: readonly StoredStrategyMastery[],
    newStrategyEvidence: readonly StrategyEvidence[] = [],
  ): Promise<readonly AchievementAward[]> {
    const [storedSkills, storedStrategies, unlocks, evidenceGroups] = await Promise.all([
      this.#repository.listSkillMastery(profileId),
      this.#repository.listStrategyMastery(profileId),
      this.#repository.listAchievementUnlocks(profileId),
      Promise.all(STRATEGIES.map((strategy) =>
        this.#repository.listStrategyEvidence(profileId, strategy.id)
      )),
    ]);
    const skillMasteryById = new Map<SkillId, SkillMastery>(
      storedSkills.map((mastery) => [mastery.skillId, mastery]),
    );
    for (const mastery of changedSkills) skillMasteryById.set(mastery.skillId, mastery);
    const strategyMasteryById = new Map<StrategyId, StrategyMastery>(
      storedStrategies.map((mastery) => [mastery.strategyId, mastery]),
    );
    for (const mastery of changedStrategies) strategyMasteryById.set(mastery.strategyId, mastery);

    const evidenceRefsBySkillId = new Map<SkillId, readonly string[]>();
    for (const mastery of skillMasteryById.values()) {
      evidenceRefsBySkillId.set(
        mastery.skillId,
        attempts.filter((attempt) => attempt.targetSkillId === mastery.skillId).map((attempt) => attempt.id),
      );
    }
    const allStrategyEvidence = [...evidenceGroups.flat(), ...newStrategyEvidence];
    const evidenceRefsByStrategyId = new Map<StrategyId, readonly string[]>();
    for (const mastery of strategyMasteryById.values()) {
      evidenceRefsByStrategyId.set(
        mastery.strategyId,
        allStrategyEvidence.filter((evidence) => evidence.strategyId === mastery.strategyId).map((evidence) => evidence.id),
      );
    }
    return evaluateAchievements(ACHIEVEMENT_CATALOG, {
      profileId, evaluatedAt: now, skillMasteryById, strategyMasteryById, attempts,
      existingCardIds: new Set(unlocks.map((unlock) => unlock.cardId)),
      evidenceRefsBySkillId, evidenceRefsByStrategyId,
    });
  }

  private async requireActiveSession(sessionId: string): Promise<SessionRecord> {
    const session = await this.#repository.getSession(sessionId);
    if (!session || (session.phase !== "CORE_ACTIVE" && session.phase !== "BONUS_ACTIVE")) {
      throw new Error(`Sessie ${sessionId} is niet actief.`);
    }
    return session;
  }

  private async requireOpenPresentation(presentationId: string): Promise<ItemPresentation> {
    const presentation = await this.#repository.getPresentation(presentationId);
    if (!presentation || presentation.state !== "PRESENTED") {
      throw new Error(`Presentatie ${presentationId} is niet open.`);
    }
    return presentation;
  }

  private async requireDecision(decisionId: string): Promise<SelectorDecisionRecord> {
    const decision = await this.#repository.getSelectorDecision(decisionId);
    if (!decision) throw new Error(`Selectorbesluit ${decisionId} bestaat niet.`);
    return decision;
  }
}
